import { useLayoutEffect } from 'react';
import { useShowcaseContext } from '../../providers/ShowcaseProvider/ShowcaseProvider';
import type { ShowcaseName } from '../../tokens/showcase';
import type { IShowcaseTriggerProps } from './types';

/**
 * Hook to trigger a showcase when a component mounts.
 *
 * The showcase will only be shown if it hasn't been seen before.
 * To show a showcase regardless of whether it's been seen, use `openShowcaseFromHistory`.
 *
 * @param showcase - Name of the showcase to trigger
 * @param enable - Whether to trigger the showcase (defaults to true)
 * @param options - Additional options (delay before showing)
 *
 * @example
 * ```tsx
 * // Show showcase when component mounts
 * useShowcase('v2.55.0 Updated navigation sidebar');
 *
 * // Show showcase conditionally
 * useShowcase('my-showcase', isFeatureEnabled);
 *
 * // Show showcase with delay
 * useShowcase('my-showcase', true, { delay: 1000 });
 * ```
 */
export function useShowcase(
  showcase: ShowcaseName,
  enable = true,
  { delay = 500 }: Pick<IShowcaseTriggerProps, 'delay'> = {}
) {
  const { openShowcase } = useShowcaseContext();

  useLayoutEffect(() => {
    if (!enable) {
      return;
    }

    let isMounted = true;

    if (delay === 0) {
      openShowcase(showcase);
    } else {
      const timeoutId = setTimeout(() => {
        if (isMounted) {
          openShowcase(showcase);
        }
      }, delay);

      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
      };
    }

    return () => {
      isMounted = false;
    };
  }, [openShowcase, enable, showcase, delay]);
}
